import React, { useState, useEffect } from 'react';
import { IconButton, Typography, Switch, FormControlLabel, Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import SearchableDropdown from 'allGlobalComponents/SearchableDropdown';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import moment from 'moment';

const DarkModeSwitch = withStyles((theme) => ({
    switchBase: {
        color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.grey[400],
        '&$checked': {
            color: theme.palette.type === 'dark' ? '#60A5FA' : theme.palette.primary.main,
        },
        '&$checked + $track': {
            backgroundColor: theme.palette.type === 'dark' ? '#3B82F6' : theme.palette.primary.main,
        },
    },
    checked: {},
    track: {
        backgroundColor: theme.palette.type === 'dark' ? '#6B7280' : theme.palette.grey[400],
    },
}))(Switch);

const useStyles = makeStyles(theme => ({
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        padding: theme.spacing(2)
    },
    fieldRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5)
    },
    fieldLabel: {
        fontSize: '1.2rem',
        color: theme.palette.text.secondary,
        fontWeight: 500
    },
    requiredStar: {
        color: theme.palette.error.main,
        marginLeft: 2
    },
    attachmentsContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        marginTop: theme.spacing(2)
    },
    attachmentItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    },
    attachmentIcon: {
        color: theme.palette.text.secondary
    },
    attachmentInfo: {
        display: 'flex',
        flexDirection: 'column'
    },
    attachmentName: {
        fontSize: '0.875rem',
        color: theme.palette.text.primary
    },
    attachmentSize: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary
    },
    attachmentRemove: {
        marginLeft: theme.spacing(1),
        padding: 2
    },
    bottomActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%'
    },
    leftActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5)
    },
    rightActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    previewContainer: {
        padding: theme.spacing(2),
        minHeight: 200,
        maxHeight: 400,
        overflow: 'auto',
        backgroundColor: theme.palette.type === 'dark' ? '#111827' : '#FFFFFF',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8
    },
    previewContent: {
        color: theme.palette.text.primary,
        fontSize: 15,
        lineHeight: 1.7,
        '& img': {
            maxWidth: '100%',
            height: 'auto'
        },
        '& a': {
            color: theme.palette.primary.main
        },
        '& ul, & ol': {
            paddingLeft: theme.spacing(3),
            marginLeft: theme.spacing(2)
        },
        '& li': {
            marginBottom: theme.spacing(0.5)
        }
    },
    characterCount: {
        padding: theme.spacing(0.5, 1),
        fontSize: '1.2rem',
        color: theme.palette.type === 'dark' ? '#9CA3AF' : theme.palette.text.secondary,
        textAlign: 'right'
    },
    characterCountWarning: {
        color: theme.palette.warning.main
    },
    characterCountError: {
        color: theme.palette.error.main
    },
    scheduleSection: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        borderRadius: 8,
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
        border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
    },
    scheduleHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1)
    },
    scheduleTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: 14,
        fontWeight: 600,
        color: theme.palette.type === 'dark' ? '#93C5FD' : '#2563EB'
    },
    dateTimeRow: {
        display: 'flex',
        gap: theme.spacing(2),
        marginTop: theme.spacing(1.5)
    },
    dateTimeField: {
        flex: 1,
        '& .MuiOutlinedInput-root': {
            '& input': {
                color: theme.palette.text.primary,
                '&::-webkit-calendar-picker-indicator': {
                    filter: theme.palette.type === 'dark' ? 'invert(1)' : 'none',
                    cursor: 'pointer'
                }
            }
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary
        }
    },
    paramsInfoBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5),
        borderRadius: 8,
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
        border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.2)'}`,
    },
    paramsInfoTitle: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.type === 'dark' ? '#4ADE80' : '#16A34A',
        marginBottom: theme.spacing(0.5)
    },
    paramsInfoText: {
        fontSize: 12,
        color: theme.palette.text.secondary
    },
    paramChip: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 16,
        fontSize: 12,
        fontWeight: 500,
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)',
        color: theme.palette.type === 'dark' ? '#4ADE80' : '#16A34A',
        marginRight: theme.spacing(0.5),
        marginTop: theme.spacing(0.5)
    },
    recipientInfoBox: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1.5),
        borderRadius: 8,
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
        border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
    },
    recipientInfoTitle: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.type === 'dark' ? '#93C5FD' : '#2563EB',
        marginBottom: theme.spacing(0.5)
    },
    recipientInfoText: {
        fontSize: 12,
        color: theme.palette.text.secondary
    },
    loadingText: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        fontStyle: 'italic'
    },
    previewLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(2)
    },
    twoColumnRow: {
        display: 'flex',
        gap: theme.spacing(2),
        '& > div': {
            flex: 1
        }
    }
}));

function ComposeCampaignModal({
    open,
    onClose,
    onSend,
    isEditMode = false,
    editingCampaign = null,
    userData = null
}) {
    const classes = useStyles();

    const [senderDomains, setSenderDomains] = useState([]);
    const [loadingDomains, setLoadingDomains] = useState(false);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [senderEmails, setSenderEmails] = useState([]);
    const [loadingSenderEmails, setLoadingSenderEmails] = useState(false);
    const [selectedSenderEmail, setSelectedSenderEmail] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateParams, setTemplateParams] = useState([]);
    const [loadingParams, setLoadingParams] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [selectedRecipient, setSelectedRecipient] = useState(null);
    const [recipientNumbers, setRecipientNumbers] = useState([]);
    const [loadingNumbers, setLoadingNumbers] = useState(false);
    const [mappedParamValues, setMappedParamValues] = useState([]);
    const [formData, setFormData] = useState({ subject: '' });
    const [attachments, setAttachments] = useState([]);
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [subjectLength, setSubjectLength] = useState(0);
    const [isScheduled, setIsScheduled] = useState(false);
    const [scheduleDate, setScheduleDate] = useState(moment().add(5, 'minutes').format('YYYY-MM-DD'));
    const [scheduleTime, setScheduleTime] = useState(moment().add(5, 'minutes').format('HH:mm'));
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const fetchSenderDomains = () => {
        setLoadingDomains(true);
        const apiPayload = {};
        if (userData?.id) apiPayload.clientId = userData?.id;
        CoreHttpHandler.request('channels', 'emailSenderDomainList', apiPayload,
            response => {
                const domains = response.data.data.data.domainsList || [];
                setSenderDomains(domains);
                setLoadingDomains(false);
            },
            error => {
                console.error('Error fetching sender domains:', error);
                setSenderDomains([]);
                setLoadingDomains(false);
            }
        );
    };

    const fetchSenderEmailsByDomain = (domainId) => {
        setLoadingSenderEmails(true);
        setSenderEmails([]);
        setSelectedSenderEmail(null);

        const apiPayload = {
            page: 0,
            limit: 999999,
            senderDomainId: domainId.toString()
        };
        if (userData?.id) apiPayload.clientId = userData?.id;

        CoreHttpHandler.request('emailCampaign', 'senderEmailFetchByDomain', apiPayload,
            response => {
                const emails = response.data?.data?.data?.emailsList || [];
                setSenderEmails(emails);
                setLoadingSenderEmails(false);
            },
            error => {
                console.error('Error fetching sender emails:', error);
                setSenderEmails([]);
                setLoadingSenderEmails(false);
            }
        );
    };

    const fetchRecipients = () => {
        setLoadingRecipients(true);
        const apiPayload = { recipients_type: 'emails', channel_type: 'email' };
        if (userData?.id) apiPayload.clientId = userData?.id;
        CoreHttpHandler.request('templateMessagesMFMS', 'fetch_recipients', apiPayload,
            response => {
                const list = response.data?.data?.data || [];
                setRecipients(list);
                setLoadingRecipients(false);
            },
            error => {
                console.error('Error fetching recipients:', error);
                setRecipients([]);
                setLoadingRecipients(false);
            }
        );
    };

    const fetchTemplates = () => {
        setLoadingTemplates(true);
        const apiPayload = { page: 0, limit: 999999 };
        if (userData?.id) apiPayload.clientId = userData?.id;
        CoreHttpHandler.request('emailCampaign', 'emailTemplateList', apiPayload,
            response => {
                const templateList = response.data?.data?.data?.emailTemplates || [];
                setTemplates(templateList);
                setLoadingTemplates(false);
            },
            error => {
                console.error('Error fetching templates:', error);
                setTemplates([]);
                setLoadingTemplates(false);
            }
        );
    };

    const fetchTemplateParams = (templateId) => {
        setLoadingParams(true);
        const apiPayload = { id: templateId };
        if (userData?.id) apiPayload.clientId = userData?.id;
        CoreHttpHandler.request('emailCampaign', 'emailTemplateParams', apiPayload,
            response => {
                const params = response.data?.data?.data || [];
                setTemplateParams(params);
                setLoadingParams(false);
            },
            error => {
                console.error('Error fetching template params:', error);
                setTemplateParams([]);
                setLoadingParams(false);
            }
        );
    };

    const fetchRecipientNumbers = (recipientId) => {
        setLoadingNumbers(true);
        const apiPayload = { recipient_id: recipientId, recipient_type: 'emails' };
        if (userData?.id) apiPayload.clientId = userData?.id;
        CoreHttpHandler.request('templateMessagesMFMS', 'fetchRecipientNumbers', apiPayload,
            response => {
                const numbers = response.data?.data?.data?.numbers || [];
                setRecipientNumbers(numbers);
                if (numbers.length > 0) {
                    const firstRecord = numbers[0];
                    const paramKeys = Object.keys(firstRecord)
                        .filter(key => key.startsWith('your_column_name_'))
                        .sort((a, b) => {
                            const numA = parseInt(a.split('_').pop());
                            const numB = parseInt(b.split('_').pop());
                            return numA - numB;
                        });
                    const values = paramKeys.map(key => ({ value: firstRecord[key] || '' }));
                    setMappedParamValues(values);
                } else {
                    setMappedParamValues([]);
                }
                setLoadingNumbers(false);
            },
            error => {
                console.error('Error fetching recipient numbers:', error);
                setRecipientNumbers([]);
                setMappedParamValues([]);
                setLoadingNumbers(false);
            }
        );
    };

    useEffect(() => {
        if (open) {
            fetchSenderDomains();
            fetchTemplates();
            fetchRecipients();
            if (!isEditMode) {
                resetForm();
            }
        }
    }, [open]);

    useEffect(() => {
        if (open && isEditMode && editingCampaign && senderDomains.length > 0) {
            // Set domain
            const editDomain = senderDomains.find(d => d.id.toString() === editingCampaign.sender_domain_id?.toString());
            if (editDomain) {
                setSelectedDomain({
                    value: editDomain.id,
                    label: `${editDomain.name} (${editDomain.domain})`
                });
                fetchSenderEmailsByDomain(editDomain.id);
            }

            setFormData({
                subject: editingCampaign.email_subject || ''
            });
            setSubjectLength(editingCampaign.email_subject?.length || 0);

            const editRecipient = recipients.find(r => r.id.toString() === editingCampaign.recipient_id?.toString());
            if (editRecipient) {
                setSelectedRecipient({
                    value: editRecipient.id.toString(),
                    label: editRecipient.name || `Recipient List ${editRecipient.id}`
                });
                fetchRecipientNumbers(editRecipient.id.toString());
            }

            setIsScheduled(editingCampaign.campaign_type === 'schedule');
            if (editingCampaign.time) {
                const scheduledTime = moment(editingCampaign.time);
                setScheduleDate(scheduledTime.format('YYYY-MM-DD'));
                setScheduleTime(scheduledTime.format('HH:mm'));
            }
            if (editingCampaign.attachments && Array.isArray(editingCampaign.attachments)) {
                setAttachments(editingCampaign.attachments.map((att, i) => {
                    if (typeof att === 'string') {
                        return {
                            id: i,
                            name: att.split('/').pop(),
                            url: att
                        };
                    }
                    return {
                        id: i,
                        name: att.filename || att.url?.split('/').pop() || 'attachment',
                        url: att.url
                    };
                }));
            }
        }
    }, [open, isEditMode, editingCampaign, senderDomains, recipients]);

    // Set sender email after emails are loaded in edit mode
    useEffect(() => {
        if (isEditMode && editingCampaign && senderEmails.length > 0) {
            const editSenderEmail = senderEmails.find(e => e.id.toString() === editingCampaign.sender_email_id?.toString());
            if (editSenderEmail) {
                setSelectedSenderEmail({
                    value: editSenderEmail.id,
                    label: editSenderEmail.email
                });
            }
        }
    }, [senderEmails, isEditMode, editingCampaign]);

    const resetForm = () => {
        setFormData({ subject: '' });
        setSubjectLength(0);
        setAttachments([]);
        setSelectedDomain(null);
        setSelectedSenderEmail(null);
        setSenderEmails([]);
        setSelectedTemplate(null);
        setSelectedRecipient(null);
        setTemplateParams([]);
        setRecipientNumbers([]);
        setMappedParamValues([]);
        setIsScheduled(false);
        setScheduleDate(moment().add(5, 'minutes').format('YYYY-MM-DD'));
        setScheduleTime(moment().add(5, 'minutes').format('HH:mm'));
        setSnackbar({ open: false, message: '', severity: 'success' });
    };

    const handleDomainChange = (newValue) => {
        setSelectedDomain(newValue);
        setSelectedSenderEmail(null);
        setSenderEmails([]);

        if (newValue) {
            fetchSenderEmailsByDomain(newValue.value);
        }
    };

    const handleSenderEmailChange = (newValue) => {
        setSelectedSenderEmail(newValue);
    };

    const formattedDomainOptions = senderDomains.map(d => ({
        value: d.id,
        label: `${d.name} (${d.domain})`
    }));

    const formattedSenderEmailOptions = senderEmails.map(e => ({
        value: e.id,
        label: e.email
    }));

    const handleTemplateChange = (newValue) => {
        setSelectedTemplate(newValue);
        if (newValue) {
            fetchTemplateParams(newValue.value);
        } else {
            setTemplateParams([]);
        }
    };

    const handleRecipientChange = (newValue) => {
        setSelectedRecipient(newValue);
        if (newValue) {
            fetchRecipientNumbers(newValue.value);
        } else {
            setRecipientNumbers([]);
            setMappedParamValues([]);
        }
    };

    const formattedTemplateOptions = templates.map(t => ({
        value: t.id,
        label: t.email_template_name
    }));

    const formattedRecipientOptions = recipients.map(r => ({
        value: r.id.toString(),
        label: `${r.id} - ${r.name || 'Unnamed List'} (${r.records_count || 0} emails)`
    }));

    const handleFieldChange = (field) => (e) => {
        const value = e.target.value;
        if (field === 'subject') {
            if (value.length <= 100) {
                setFormData(prev => ({ ...prev, [field]: value }));
                setSubjectLength(value.length);
            }
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const uploadFileToServer = (file) => {
        return new Promise((resolve, reject) => {
            const form = new FormData();
            form.append('file', file, `${Date.now()}_${file.name.replace(/\s/g, '')}`);
            CoreHttpHandler.request('content', 'upload', { params: form },
                res => resolve(res.data.data.link),
                error => reject(error)
            );
        });
    };

    const handleAttachFile = async () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.onchange = async (e) => {
            const files = Array.from(e.target.files);
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes

            setUploadingAttachment(true);
            for (const file of files) {
                // Check file size
                if (file.size > maxSize) {
                    showSnackbar(`File "${file.name}" exceeds 10MB limit. Skipping...`, 'error');
                    continue;
                }

                try {
                    const uploadedUrl = await uploadFileToServer(file);
                    const newAttachment = {
                        id: Date.now() + Math.random(),
                        name: file.name,
                        size: formatFileSize(file.size),
                        url: uploadedUrl
                    };
                    setAttachments(prev => [...prev, newAttachment]);
                } catch (error) {
                    console.error('Failed to upload attachment:', error);
                    showSnackbar(`Failed to upload "${file.name}"`, 'error');
                }
            }
            setUploadingAttachment(false);
        };
        input.click();
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleRemoveAttachment = (id) => {
        setAttachments(prev => prev.filter(att => att.id !== id));
    };

    const getPreviewHtml = () => {
        if (!selectedTemplate) return '';
        const template = templates.find(t => t.id === selectedTemplate.value);
        if (!template) return '';

        let html = template.email_template_html || '';
        const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        if (bodyMatch) {
            html = bodyMatch[1];
        }

        if (mappedParamValues.length > 0) {
            mappedParamValues.forEach((param, index) => {
                const placeholder = `{{${index + 1}}}`;
                const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');
                // Wrap the replacement value in a styled span
                const styledValue = param.value
                    ? `<span style="background-color: #22c55e; color: white; padding: 2px 8px; border-radius: 4px; font-weight: 500; display: inline-block;">${param.value}</span>`
                    : placeholder;
                html = html.replace(regex, styledValue);
            });
        } else {
            // Highlight unreplaced parameters
            html = html.replace(/\{\{\d+\}\}/g, (match) => {
                return `<span style="background-color: #22c55e; color: white; padding: 2px 8px; border-radius: 4px; font-weight: 500; display: inline-block;">${match}</span>`;
            });
        }

        return html;
    };

    const isScheduleTimeValid = () => {
        if (!isScheduled) return true;
        const scheduledDateTime = moment(`${scheduleDate} ${scheduleTime}`, 'YYYY-MM-DD HH:mm');
        return scheduledDateTime.isAfter(moment());
    };

    const handleSend = async () => {
        if (!selectedDomain) {
            showSnackbar('Please select a sender domain', 'error');
            return;
        }

        if (!selectedSenderEmail) {
            showSnackbar('Please select a sender email', 'error');
            return;
        }

        if (!selectedTemplate) {
            showSnackbar('Please select a template', 'error');
            return;
        }

        if (!selectedRecipient) {
            showSnackbar('Please select a recipient list', 'error');
            return;
        }

        if (isScheduled && !isScheduleTimeValid()) {
            showSnackbar('Cannot schedule campaign in the past. Please select a future date and time.', 'error');
            return;
        }

        setSendingEmail(true);

        try {
            let scheduleDateTime = null;

            if (isScheduled) {
                scheduleDateTime = moment(`${scheduleDate} ${scheduleTime}`, 'YYYY-MM-DD HH:mm').toISOString();
            } else {
                scheduleDateTime = moment().add(5, 'minutes').toISOString();
            }

            const emailParams = {
                parameters: mappedParamValues.length > 0
                    ? mappedParamValues
                    : templateParams.map(() => ({ value: '' }))
            };

            const payload = {
                sender_domain_id: selectedDomain.value.toString(),
                sender_email_id: selectedSenderEmail.value.toString(),
                recipient_id: parseInt(selectedRecipient.value),
                email_template_id: selectedTemplate.value.toString(),
                email_subject: formData.subject || '',
                email_params: emailParams,
                campaign_type: 'schedule',
                time: scheduleDateTime
            };

            // Format attachments as API expects: [{ url: "...", filename: "..." }]
            if (attachments.length > 0) {
                payload.attachments = attachments.map(att => ({
                    url: att.url,
                    filename: att.name
                }));
            }

            if (isEditMode && editingCampaign) {
                payload.id = editingCampaign.id.toString();
            }

            if (userData?.id) {
                payload.clientId = userData?.id;
            }

            const apiMethod = isEditMode ? 'emailsCampaignUpdate' : 'emailsCampaignCreate';

            CoreHttpHandler.request('emailCampaign', apiMethod, payload,
                response => {
                    setSendingEmail(false);
                    const successMessage = isEditMode
                        ? 'Campaign updated successfully!'
                        : isScheduled
                            ? 'Campaign scheduled successfully!'
                            : 'Campaign created successfully!';
                    if (onSend) onSend(response.data, successMessage);
                    onClose();
                },
                error => {
                    console.error('Error with campaign:', error);
                    showSnackbar(`Failed to ${isEditMode ? 'update' : 'create'} campaign. Please try again.`, 'error');
                    setSendingEmail(false);
                }
            );
        } catch (error) {
            console.error('Error with campaign:', error);
            showSnackbar(`Failed to ${isEditMode ? 'update' : 'create'} campaign. Please try again.`, 'error');
            setSendingEmail(false);
        }
    };

    const isFormValid = () => {
        return (
            selectedDomain &&
            selectedSenderEmail &&
            selectedTemplate &&
            selectedRecipient &&
            !uploadingAttachment
        );
    };

    const modalContent = (
        <div className={classes.formContainer}>
            {/* Sender Domain and Sender Email Row */}
            <div className={classes.twoColumnRow}>
                <div className={classes.fieldRow}>
                    <Typography className={classes.fieldLabel}>
                        Sender Domain <span className={classes.requiredStar}>*</span>
                    </Typography>
                    <SearchableDropdown
                        label=""
                        options={formattedDomainOptions}
                        value={selectedDomain}
                        onChange={handleDomainChange}
                        placeholder={loadingDomains ? "Loading domains..." : "Select sender domain..."}
                        fullWidth
                        disabled={loadingDomains}
                    />
                </div>

                <div className={classes.fieldRow}>
                    <Typography className={classes.fieldLabel}>
                        Sender Email <span className={classes.requiredStar}>*</span>
                    </Typography>
                    <SearchableDropdown
                        label=""
                        options={formattedSenderEmailOptions}
                        value={selectedSenderEmail}
                        onChange={handleSenderEmailChange}
                        placeholder={
                            !selectedDomain
                                ? "Select domain first..."
                                : loadingSenderEmails
                                    ? "Loading emails..."
                                    : senderEmails.length === 0
                                        ? "No emails found"
                                        : "Select sender email..."
                        }
                        fullWidth
                        disabled={!selectedDomain || loadingSenderEmails || senderEmails.length === 0}
                    />
                    {loadingSenderEmails && (
                        <Typography className={classes.loadingText}>Loading sender emails...</Typography>
                    )}
                </div>
            </div>

            <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>
                    Recipients <span className={classes.requiredStar}>*</span>
                </Typography>
                <SearchableDropdown
                    label=""
                    options={formattedRecipientOptions}
                    value={selectedRecipient}
                    onChange={handleRecipientChange}
                    placeholder={loadingRecipients ? "Loading recipients..." : "Search recipient list..."}
                    fullWidth
                    disabled={loadingRecipients}
                />
                {loadingRecipients && (
                    <Typography className={classes.loadingText}>Loading recipients list...</Typography>
                )}
                {loadingNumbers && (
                    <Typography className={classes.loadingText}>Loading recipient data...</Typography>
                )}
                {selectedRecipient && recipientNumbers.length > 0 && (
                    <div className={classes.recipientInfoBox}>
                        <Typography className={classes.recipientInfoTitle}>
                            Recipient Data ({recipientNumbers.length} emails)
                        </Typography>
                        {mappedParamValues.length > 0 && (
                            <Typography className={classes.recipientInfoText}>
                                Parameters will be mapped from: {mappedParamValues.map((p, i) => `{{${i + 1}}} = "${p.value}"`).join(', ')}
                            </Typography>
                        )}
                    </div>
                )}
            </div>

            <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>
                    Subject <span className={classes.requiredStar}>*</span>
                </Typography>
                <FormFieldOmni
                    variant="outlined"
                    value={formData.subject}
                    onChange={handleFieldChange('subject')}
                    size="small"
                    fullWidth
                    placeholder="Email subject"
                    inputProps={{ maxLength: 100 }}
                />
                <Typography
                    className={`${classes.characterCount} ${subjectLength >= 100
                        ? classes.characterCountError
                        : subjectLength >= 80
                            ? classes.characterCountWarning
                            : ''
                        }`}
                >
                    {subjectLength}/100 characters
                </Typography>
            </div>

            <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>
                    Email Template <span className={classes.requiredStar}>*</span>
                </Typography>
                <SearchableDropdown
                    label=""
                    options={formattedTemplateOptions}
                    value={selectedTemplate}
                    onChange={handleTemplateChange}
                    placeholder="Search and select template..."
                    fullWidth
                />
                {loadingParams && (
                    <Typography className={classes.loadingText}>Loading template parameters...</Typography>
                )}
                {selectedTemplate && templateParams.length > 0 && (
                    <div className={classes.paramsInfoBox}>
                        <Typography className={classes.paramsInfoTitle}>
                            Template Parameters ({templateParams.length})
                        </Typography>
                        <Typography className={classes.paramsInfoText}>
                            This template has {templateParams.length} parameter(s) that will be filled from recipient data.
                        </Typography>
                        <div>
                            {templateParams.map((_, index) => (
                                <span key={index} className={classes.paramChip}>
                                    {`{{${index + 1}}}`}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className={classes.scheduleSection}>
                <div className={classes.scheduleHeader}>
                    <div className={classes.scheduleTitle}>
                        <ScheduleIcon style={{ fontSize: 20 }} />
                        Schedule Campaign
                    </div>
                    <FormControlLabel
                        control={
                            <DarkModeSwitch
                                checked={isScheduled}
                                onChange={(e) => setIsScheduled(e.target.checked)}
                                size="small"
                            />
                        }
                        label=""
                    />
                </div>
                <Typography style={{ fontSize: 12, color: '#6B7280' }}>
                    {isScheduled
                        ? 'Set a specific date and time to send your campaign'
                        : 'Campaign will be sent in 5 minutes'
                    }
                </Typography>

                {isScheduled && (
                    <div className={classes.dateTimeRow}>
                        <div className={classes.dateTimeField}>
                            <FormFieldOmni
                                variant="outlined"
                                type="date"
                                value={scheduleDate}
                                onChange={(e) => setScheduleDate(e.target.value)}
                                size="small"
                                fullWidth
                                label="Date"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: moment().format('YYYY-MM-DD') }}
                            />
                        </div>
                        <div className={classes.dateTimeField}>
                            <FormFieldOmni
                                variant="outlined"
                                type="time"
                                value={scheduleTime}
                                onChange={(e) => setScheduleTime(e.target.value)}
                                size="small"
                                fullWidth
                                label="Time"
                                InputLabelProps={{ shrink: true }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {selectedTemplate && (
                <>
                    <Typography className={classes.previewLabel}>Preview</Typography>
                    <div className={classes.previewContainer}>
                        <div
                            className={classes.previewContent}
                            dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                        />
                    </div>
                </>
            )}

            {attachments.length > 0 && (
                <div className={classes.attachmentsContainer}>
                    {attachments.map(attachment => (
                        <div key={attachment.id} className={classes.attachmentItem}>
                            <InsertDriveFileOutlinedIcon className={classes.attachmentIcon} />
                            <div className={classes.attachmentInfo}>
                                <Typography className={classes.attachmentName}>
                                    {attachment.name}
                                </Typography>
                                {attachment.size && (
                                    <Typography className={classes.attachmentSize}>
                                        {attachment.size}
                                    </Typography>
                                )}
                            </div>
                            <IconButton
                                size="small"
                                className={classes.attachmentRemove}
                                onClick={() => handleRemoveAttachment(attachment.id)}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>
                    ))}
                </div>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );

    const modalActions = (
        <div className={classes.bottomActions}>
            <div className={classes.leftActions}>
                <ButtonOmni
                    onClick={handleAttachFile}
                    size="small"
                    label={"Attachments"}
                    startIcon={<AttachFileIcon />}
                    disabled={uploadingAttachment}
                />
            </div>
            <div className={classes.rightActions}>
                <ButtonOmni
                    onClick={handleSend}
                    size="small"
                    label={sendingEmail ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Campaign' : 'Create Campaign')}
                    loading={sendingEmail}
                    disabled={!isFormValid() || sendingEmail}
                />
            </div>
        </div>
    );

    return (
        <ModalOmni
            open={open}
            handleClose={onClose}
            title={isEditMode ? "Edit Campaign" : "New Campaign"}
            content={modalContent}
            actions={modalActions}
            customWidth={700}
            disableBackdropClick={true}
            disableEscapeKeyDown={true}
        />
    );
}

export default ComposeCampaignModal;